import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Truck, Package, Globe } from "lucide-react";
import { toast } from "sonner";
import ProductCard from "../components/ProductCard";
import SizeGuide from "../components/SizeGuide";
import { getProduct, PRODUCTS, SIZES } from "../mock/mock";
import { useCart } from "../context/CartContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = getProduct(slug);
  const { addItem } = useCart();
  const [size, setSize] = useState(null);

  if (!product) {
    return (
      <main className="pt-32 pb-32 text-center">
        <h1 className="font-display text-4xl text-white">Piece not found</h1>
        <Link to="/shop" className="inline-block mt-6 text-neon underline">Back to the shop</Link>
      </main>
    );
  }

  const related = PRODUCTS.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, 4);
  const price = product.sale || product.price;

  const addToBag = () => {
    if (!size) {
      toast("Pick a size", { description: "Select a size before adding to your bag." });
      return;
    }
    addItem(product, size, 1);
    toast("Added to bag", { description: `${product.name} · Size ${size}` });
  };

  return (
    <main className="pt-16 md:pt-[72px]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-white/60 hover:text-neon transition"
        >
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 grid md:grid-cols-2 gap-8 lg:gap-14">
        {/* Image */}
        <div className="relative rounded-xl overflow-hidden border border-white/10 bg-[#0e0e10]">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover aspect-[4/5]" />
          {product.tag && (
            <span className="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded bg-neon text-black">
              {product.tag}
            </span>
          )}
        </div>

        {/* Info */}
        <div className="md:py-4">
          <p className="text-xs uppercase tracking-[0.2em] text-white/45">
            {product.brand} · {product.category}
          </p>
          <h1 className="font-display text-4xl md:text-6xl text-white mt-2 leading-none">{product.name}</h1>
          <div className="mt-4 flex items-center gap-3">
            {product.sale ? (
              <>
                <span className="text-2xl text-white/40 line-through">${product.price}.00</span>
                <span className="text-3xl font-bold text-neon">${product.sale}.00</span>
              </>
            ) : (
              <span className="text-3xl font-bold text-white">${product.price}.00</span>
            )}
          </div>

          <div className="mt-3">
            {product.pod === false ? (
              <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/70 border border-white/25 rounded-full px-3 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white/60" /> Coming soon
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-neon border border-neon/40 rounded-full px-3 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-neon" /> Print-on-demand · Made to order
              </span>
            )}
          </div>

          <p className="mt-6 text-white/65 leading-relaxed max-w-lg">
            {product.desc ||
              "Print-on-demand streetwear from the drop. Heavyweight fabric, boxy fit, and the infinity mark done right."}
          </p>

          {product.pod === false ? (
            <div className="mt-8 rounded-xl border border-white/12 bg-white/5 p-6">
              <p className="font-display text-2xl text-white">Dropping soon</p>
              <p className="mt-1 text-sm text-white/60 max-w-md">
                This piece isn't in the print-on-demand line yet. Join the alert and we'll ping you the moment it drops.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  disabled
                  className="bg-white/10 text-white/40 font-bold uppercase tracking-[0.16em] text-sm px-10 py-4 rounded-full cursor-not-allowed"
                >
                  Coming Soon
                </button>
                <button
                  onClick={() => toast("You're on the list", { description: `We'll alert you when ${product.name} drops (demo).` })}
                  className="border border-neon/50 text-neon font-bold uppercase tracking-[0.16em] text-sm px-8 py-4 rounded-full hover:bg-neon hover:text-black transition"
                >
                  Notify Me
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/60">Select size</p>
                  <SizeGuide category={product.category} />
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {SIZES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      className={`min-w-[52px] px-4 py-3 rounded-md border font-semibold text-sm transition ${
                        size === s
                          ? "bg-neon text-black border-neon"
                          : "bg-transparent text-white border-white/20 hover:border-white/50"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={addToBag}
                className="mt-8 w-full sm:w-auto bg-neon text-black font-bold uppercase tracking-[0.16em] text-sm px-12 py-4 rounded-full hover:brightness-95 transition"
              >
                Add to Bag
              </button>
            </>
          )}

          <ul className="mt-8 space-y-3 border-t border-white/10 pt-6">
            <li className="flex items-center gap-3 text-sm text-white/60"><Package size={16} className="text-neon" /> Print-on-demand — made fresh when you order.</li>
            <li className="flex items-center gap-3 text-sm text-white/60"><Check size={16} className="text-neon" /> Oversized streetwear fit. Size down for regular.</li>
            <li className="flex items-center gap-3 text-sm text-white/60"><Globe size={16} className="text-neon" /> Ships worldwide. Tracked delivery.</li>
          </ul>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-14">
          <h2 className="font-display text-3xl md:text-5xl text-white mb-8">More {product.category}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
